import React, { useState } from "react";
import CommonButton from "./CommonButton";
import { Edit, Trash, Copy, ChevronLeft, ChevronRight } from "lucide-react";

interface Column {
  header: string;
  accessor: string;
}

interface GridComponentProps {
  columns: Column[];
  data: Record<string, any>[];
  pageSize?: number;
  handleEditClick: (row: Record<string, any>) => void;
  handleStatusToggle: (row: Record<string, any>) => void;
  handleDuplicateClick: (row: Record<string, any>) => void;
}

const GridComponent: React.FC<GridComponentProps> = ({
  columns,
  data,
  pageSize = 10,
  handleEditClick,
  handleStatusToggle,
  handleDuplicateClick,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(data.length / pageSize) || 1;
  const startIndex = (currentPage - 1) * pageSize;
  const currentRows = data.slice(startIndex, startIndex + pageSize);

  return (
    <div className="w-full">
      {/* Table Container */}
      <div className="lg:overflow-x-auto overflow-x-scroll rounded-2xl border border-gray-200 bg-white shadow-sm">
        <table className="min-w-[900px] lg:min-w-full">
          {/* Header */}
          <thead className="bg-green-100">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.accessor}
                  className="px-4 py-4 text-left text-sm font-semibold text-black"
                >
                  {col.header}
                </th>
              ))}
              {/* Optional Actions Header if needed */}
            </tr>
          </thead>

          {/* Body */}
          <tbody className="divide-y divide-gray-100">
            {currentRows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-8 text-center text-gray-400 italic text-xs"
                >
                  No records found in this view.
                </td>
              </tr>
            ) : (
              currentRows.map((row, i) => (
                <tr
                  key={row.id || i}
                  className={`transition-all duration-200 ${
                    row.status
                      ? "hover:bg-gray-50"
                      : "bg-gray-100 opacity-60 grayscale"
                  }`}
                >
                  {columns.map((col) => (
                    <td
                      key={col.accessor}
                      className={`px-4 py-3 text-sm text-gray-700 ${
                        col.accessor === "quantity" ||
                        col.accessor === "status"
                          ? "text-center"
                          : ""
                      }`}
                    >
                      {col.accessor === "sku" ? (
                        <span className="text-sm text-logoHeading font-medium">
                          {row.sku}
                        </span>
                      ) : col.accessor === "productinfo" ? (
                        <div
                          onClick={() => handleEditClick(row)}
                          className="flex items-center gap-4 cursor-pointer group"
                        >
                          {/* Product Image */}
                          <div className="flex-shrink-0">
                            <img
                              src={row.productimage}
                              alt="product"
                              className="w-14 h-14 object-cover rounded-lg border border-gray-100"
                            />
                          </div>

                          {/* Product Details */}
                          <div className="flex flex-col">
                            <span className="text-base font-semibold text-gray-800 group-hover:text-[#2F5E2E] transition">
                              {row.productname}
                            </span>
                            <span className="text-sm font-medium text-mainBrandColor mt-0.5">
                              {row.price}
                            </span>
                          </div>
                        </div>
                      ) : col.accessor === "quantity" ? (
                        <div className="flex items-center justify-center font-bold text-logoHeading text-base">
                          {row.quantity}
                        </div>
                      ) : col.accessor === "status" ? (
                        <div className="flex items-center justify-center">
                          <button
                            type="button"
                            onClick={() => handleStatusToggle(row)}
                            className={`relative w-12 h-6 rounded-full transition-all duration-300 ${
                              row.status ? "bg-green-500" : "bg-gray-300"
                            }`}
                          >
                            <div
                              className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-md transition-all duration-300 ${
                                row.status ? "translate-x-7" : "translate-x-1"
                              }`}
                            />
                          </button>
                        </div>
                      ) : col.accessor === "userType" ? (
                        <span className="text-sm text-black">{row.userType}</span>
                      ) : col.accessor === "cartPrice" ? (
                        <span className="text-sm text-mainBrandColor">
                          {row.cartPrice}
                        </span>
                      ) : col.accessor === "actions" ? (
                        <div className="flex items-center gap-2">
                      
                            <button
                              type="button"
                              onClick={() => handleDuplicateClick(row)}
                              title="Duplicate Product"
                              className="p-1.5 bg-slate-100 hover:bg-emerald-50 text-slate-600 hover:text-emerald-700 rounded-lg transition"
                            >
                              <Copy size={14} />
                            </button>
                       
                          <button
                            type="button"
                            onClick={() => handleEditClick(row)}
                            title="Edit Product"
                            className="p-1.5 bg-slate-100 hover:bg-blue-50 text-slate-600 hover:text-blue-700 rounded-lg transition"
                          >
                            <Edit size={14} />
                          </button>
                        </div>
                      ) : (
                        row[col.accessor]
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center px-2 mt-4 text-xs font-medium text-slate-500">
          <div>
            Showing page <span className="font-bold text-slate-800">{currentPage}</span> of{" "}
            <span className="font-bold text-slate-800">{totalPages}</span>
          </div>
          <div className="flex items-center gap-2">
            <CommonButton
              text="Prev"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              rounded="md"
              textContainerClassName="px-3 py-1.5 text-xs"
              variant="filled"
            />
            <CommonButton
              text="Next"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              rounded="md"
              textContainerClassName="px-3 py-1.5 text-xs"
              variant="filled"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default GridComponent;